/** ワークフローの画面が共通に使う問い合わせと小道具。 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { WfRequest, WfTask } from "../../lib/api";
import { hasRole } from "../../lib/roles";
import { useApi, useMe } from "../../lib/useApi";

/** 書き込みの可否。ボタンの出し分けだけに使う(権限の真実は API 側)。 */
export function useWfPerms() {
  const me = useMe();
  return {
    me: me.data,
    /** 申請を出す・タスクを完了にする */
    canOperate: hasRole(me.data, "hr:operator"),
    /** 承認・却下 */
    canApprove: hasRole(me.data, "hr:approver"),
    /** テンプレート・カタログ */
    canAdmin: hasRole(me.data, "hr:admin"),
  };
}

/** WF まわりのキャッシュをまとめて捨てる(申請すると割当・タスク・監査が動く)。 */
export function useInvalidateWf() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["core"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

export function useCatalog(activeOnly = false) {
  const api = useApi();
  return useQuery({
    queryKey: ["core", "catalog", activeOnly],
    queryFn: () =>
      api.catalog(new URLSearchParams({ limit: "200", ...(activeOnly ? { active: "true" } : {}) })),
    staleTime: 30_000,
  });
}

export function usePartners(activeOnly = false) {
  const api = useApi();
  return useQuery({
    queryKey: ["core", "partners", activeOnly],
    queryFn: () =>
      api.partners(new URLSearchParams({ limit: "200", ...(activeOnly ? { active: "true" } : {}) })),
    staleTime: 30_000,
  });
}

/** 従業員の割当(あるべき状態)。変更申請の「無効化」候補を出すのに使う。 */
export function useAssignments(employeeId: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["core", "assignments", employeeId],
    queryFn: () =>
      api.assignments(new URLSearchParams({ limit: "200", employee_id: String(employeeId) })),
    enabled: !!employeeId,
  });
}

export function useTemplates(kind = "") {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-templates", kind],
    queryFn: () => api.wfTemplates(kind),
    staleTime: 30_000,
  });
}

export function useTemplate(id: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-template", id],
    queryFn: () => api.wfTemplate(id!),
    enabled: !!id,
  });
}

export function useWfDefaults() {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-defaults"],
    queryFn: api.wfDefaults,
    staleTime: 30_000,
  });
}

export function useRequests(params: Record<string, string>) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-requests", params],
    queryFn: () => api.wfRequests(new URLSearchParams({ limit: "100", ...params })),
  });
}

export function useRequest(id: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-request", id],
    queryFn: () => api.wfRequest(id!),
    enabled: !!id,
  });
}

export function useTasks(params: Record<string, string>) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-tasks", params],
    queryFn: () => api.wfTasks(new URLSearchParams({ limit: "100", ...params })),
  });
}

export function useExternalRequests(params: Record<string, string>) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-external", params],
    queryFn: () => api.externalRequests(new URLSearchParams({ limit: "100", ...params })),
  });
}

export function useMailLog(externalRequestId: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "wf-mail-log", externalRequestId],
    queryFn: () =>
      api.mailLog(
        new URLSearchParams({
          limit: "50",
          ...(externalRequestId ? { external_request_id: String(externalRequestId) } : {}),
        }),
      ),
    enabled: externalRequestId !== undefined,
  });
}

/** SLA を超過しているか。一覧に赤い印を出すのに使う。 */
export function isOverdue(row: WfRequest | WfTask): boolean {
  return !!row.sla?.over && !row.sla.done;
}

/** idp のコネクタが閉じたタスクの completed_by は `connector:<キー>`(wfActions と同じ)。 */
const BY_GWS = "connector:gws";

/**
 * 再発行の対象になるタスクか。自動の発行(`provision`)で、GWS のコネクタが完了にしたもの。
 * 申請由来のタスクは `connector` が空(コネクタはカタログ項目が持つ)なので、完了者で見る。
 */
export function canResetInitialPassword(t: WfTask): boolean {
  return (
    t.mode === "auto" &&
    t.action === "provision" &&
    !t.op &&
    t.status === "done" &&
    t.employee_id != null &&
    (t.connector === "gws" || t.completed_by === BY_GWS)
  );
}
